import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkedAd } from '../../api';
import { LindList } from './KindList';
import * as S from './BookmarkCard.styled';
import { AdStatus } from '@/types';
import { useIsShop } from '@/hooks/useIsShop';
import { formatNumber } from '@/utils/helpers';
import { getMessageTime } from '@/utils/datetime.utils';
import { HeartActiveIcon, HeartIcon } from '@/components/UI/Icons/Icons';
import { useBookmark } from '@/hooks/useBookmark';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useUserFavorites } from '@/features/BookmarkCollections/hooks';
import { useBookmarkSelectionStore } from '@/features/BookmarkCollections/store';
import { Typography } from '@/components/UI/Typography/Typography.styles';

interface BookmarkCardProps {
  ad?: BookmarkedAd;
  priority?: boolean;
  hasBookmarkButton?: boolean;
  onRemove?: (ad: BookmarkedAd) => void;
}

export const BookmarkCard = ({
  ad,
  onRemove,
  priority = false,
  hasBookmarkButton = true,
}: BookmarkCardProps) => {
  const isShop = useIsShop();
  const { t } = useTranslation();
  const { refetch } = useUserFavorites({ skip: true });
  const { isBookmarked, toggleBookmark } = useBookmark({ ad });
  const { toggleSelectedItem, isSelected, isSelectionActive } =
    useBookmarkSelectionStore();

  const isShopAdBookmark = !isBookmarked && isShop && ad?.shop;
  const isBookmarkButtonVisible = hasBookmarkButton && !isSelectionActive;

  const statusText = useMemo(() => {
    switch (ad?.status) {
      case AdStatus.Expired:
        return t('pages.ad.ad_inactive');
      case AdStatus.Archived:
        return t('pages.ad.ad_archived');
      default:
        return '';
    }
  }, [ad?.status]);

  const hasStatus = statusText.length > 0;

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!ad) return;

    await toggleBookmark();
    await refetch();

    if (ad.isBookmarked) {
      onRemove?.(ad);
    }
  };

  const toggleSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!ad) return;

    toggleSelectedItem(String(ad.legacyResourceId));
  };

  if (!ad) {
    return (
      <S.CardPlaceholder>
        <SkeletonLoader height="100%" />
      </S.CardPlaceholder>
    );
  }

  return (
    <S.Card
      href={ad?.path ?? ''}
      $isDisabled={hasStatus && isSelectionActive}
      onClick={isSelectionActive ? toggleSelection : undefined}
    >
      <S.ImageWrapper>
        {hasStatus ? (
          <S.StatusLabel data-testid="bookmark-card-overlay">
            <Typography.Body>{statusText}</Typography.Body>
          </S.StatusLabel>
        ) : null}
        {isBookmarkButtonVisible && (
          <S.BookmarkButton
            onClick={toggleFavorite}
            className="products-bookmarking"
            data-stat={isShopAdBookmark ? 'shop-ad-bookmarking' : undefined}
            data-testid="bookmark-button"
          >
            {isBookmarked ? <HeartActiveIcon stroke="none" /> : <HeartIcon />}
          </S.BookmarkButton>
        )}

        {!hasStatus && isSelectionActive && (
          <S.Checkbox
            onClick={toggleSelection}
            $isChecked={isSelected(String(ad.legacyResourceId))}
          />
        )}

        <S.Image
          alt={ad.title}
          title={ad.title}
          src={ad.photo.url}
          {...(priority ? { fetchPriority: 'high' } : { loading: 'lazy' })}
        />

        {ad.shop && <S.Label>{t('common.shop')}</S.Label>}
      </S.ImageWrapper>
      {ad.price !== null && (
        <S.Price>{formatNumber(ad.price, 2, true) || 0} AZN</S.Price>
      )}
      <S.Title $hasPadding={ad.price === null}>{ad.title}</S.Title>
      <S.Footer>
        <S.DateAndRegion>
          {`${ad.region}, `}
          {getMessageTime(new Date(ad.updatedAt), t, true)}
        </S.DateAndRegion>
        <LindList kinds={ad.kinds} />
      </S.Footer>
    </S.Card>
  );
};
